import { useRef, useState, useEffect, forwardRef } from 'react';
import ReactDatePicker from 'react-datepicker';
import PropTypes from 'prop-types';

import { getClassNames } from '../../../../utils/util';

import { ReactComponent as CalendarIcon } from '../../../../resources/icons/calendar.svg';

import './date-picker.scss';
import 'react-datepicker/dist/react-datepicker.css';

const DatePicker = forwardRef(
  ({ name, placeholder, value, onChange, disabled, minDate, maxDate, error, shortMonth }, ref) => {
    const [showError, setShowError] = useState(error);

    const datePickerRef = useRef();

    useEffect(() => {
      setShowError(error);
    }, [error]);

    const handleChange = (date) => {
      setShowError(false);

      if (!date) {
        onChange(null);
        return;
      }

      const offSet = date.getTimezoneOffset() / 60;
      const adjustedDate = new Date(date.setHours(0));
      adjustedDate.setHours(date.getHours() - offSet);
      onChange(adjustedDate);
    };

    const handleIconClick = () => {
      if (ref) {
        ref.current.setOpen(true);
      } else {
        datePickerRef.current.setOpen(true);
      }
    };

    return (
      <div className="date-picker">
        <div
          className={getClassNames('date-picker__input-container', { disabled, error: showError })}
          data-testid="date-picker"
        >
          <ReactDatePicker
            id={name}
            selected={value}
            onChange={handleChange}
            dateFormat={shortMonth ? 'dd MMM yyyy' : 'dd MMMM yyyy'}
            ref={ref ? ref : datePickerRef}
            showPopperArrow={false}
            disabled={disabled}
            autoComplete="off"
            minDate={minDate}
            maxDate={maxDate}
            fixedHeight={true}
            placeholderText={placeholder}
          />
          <div>
            <CalendarIcon
              className={getClassNames('date-picker__icon', { disabled })}
              onClick={handleIconClick}
              data-testid="date-picker-icon"
            />
          </div>
        </div>
      </div>
    );
  }
);

DatePicker.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.instanceOf(Date),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  inline: PropTypes.bool,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  error: PropTypes.bool,
  shortMonth: PropTypes.bool,
};

DatePicker.defaultProps = {
  label: undefined,
  name: undefined,
  placeholder: undefined,
  value: undefined,
  disabled: false,
  inline: false,
  minDate: undefined,
  maxDate: undefined,
  error: false,
  shortMonth: false,
};

export default DatePicker;
